import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Grid,
  Container,
  Rating,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Fab,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Favorite,
  FavoriteBorder,
  Share,
  Star,
  LocationOn,
  Phone,
  Email,
  AccessTime,
  ShoppingCart,
  Add,
  Remove,
  Search,
  Close,
  CheckCircle,
  StarBorder,
  ExpandMore,
  Schedule,
  Person,
  Business,
  LocalOffer,
  PhotoLibrary,
  RateReview,
  Chat,
  Timer,
  Male,
  Female,
  People,
  NavigateNext,
  NavigateBefore,
  PlayArrow,
  Pause,
  Build,
  Facebook,
  Instagram,
  LinkedIn,
  Language
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useCart } from '../contexts/CartContext';
import { providerAPI, favoritesAPI, chatAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';
import BrandLoader from '../components/BrandLoader';

// Custom styled components for unique design
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: { xs: '50vh', md: '60vh' },
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100px',
    background: 'linear-gradient(to top, rgba(255,255,255,0.1), transparent)'
  }
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: 28,
  boxShadow: '0 25px 50px rgba(0,0,0,0.12)',
  marginTop: -80,
  position: 'relative',
  zIndex: 2,
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid rgba(255,255,255,0.8)',
  backdropFilter: 'blur(10px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
    borderRadius: 28,
    zIndex: -1
  }
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid #f0f0f0',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    transform: 'scaleX(0)',
    transition: 'transform 0.3s ease'
  },
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15)',
    borderColor: '#667eea',
    '&::before': {
      transform: 'scaleX(1)'
    }
  },
  '& .MuiCardMedia-root': {
    transition: 'transform 0.3s ease'
  },
  '&:hover .MuiCardMedia-root': {
    transform: 'scale(1.05)'
  }
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: { xs: 16, md: 24 },
  right: { xs: 16, md: 24 },
  width: { xs: 48, md: 56 },
  height: { xs: 48, md: 56 },
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
  }
}));

// Mock data with different providers
const mockProviders = {
  'salon-1': {
    _id: 'salon-1',
    name: 'Glamour Salon',
    type: 'Salon',
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop',
    images: [
      'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400&h=300&fit=crop'
    ],
    rating: 4.8,
    reviewCount: 127,
    bio: 'Professional beauty salon offering premium hair and beauty services with over 10 years of experience.',
    experience: '10+',
    specializations: ['Hair Styling', 'Facial', 'Makeup'],
    address: '123 Beauty Street, Downtown',
    phone: '+1 (555) 123-4567',
    email: 'info@glamoursalon.com',
    workingHours: 'Mon-Sat: 9AM-8PM, Sun: 10AM-6PM',
    isVerified: true,
    totalBookings: 1500,
    averageRating: 4.8,
    distance: 2.5,
    inSalon: true,
    atHome: true,
    categories: ['Hair', 'Beauty', 'Nails'],
    minimum_order_value: 120,
    services: [
      {
        _id: 'service-1',
        name: 'Hair Cut & Styling',
        description: 'Professional haircut with styling and blow dry',
        price: 45,
        duration: 60,
        image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=200&h=200&fit=crop',
        mode: 'inSalon',
        category: 'Hair',
        subcategory: 'Cutting',
        rating: 4.9,
        gender: 'Both',
        featured: true
      },
      {
        _id: 'service-2',
        name: 'Facial Treatment',
        description: 'Deep cleansing facial with premium products',
        price: 65,
        duration: 90,
        image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=200&h=200&fit=crop',
        mode: 'inSalon',
        category: 'Beauty',
        subcategory: 'Facial',
        rating: 4.8,
        gender: 'Both',
        offerType: 'today'
      },
      {
        _id: 'service-3',
        name: 'Hair & Facial Combo',
        description: 'Complete hair styling with facial treatment',
        price: 95,
        duration: 120,
        image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=200&h=200&fit=crop',
        mode: 'inSalon',
        category: 'Combo',
        subcategory: 'Hair & Beauty',
        rating: 4.9,
        gender: 'Both',
        isCombo: true,
        offerType: 'week',
        selectedServices: ['Hair Cut & Styling', 'Facial Treatment'],
        comboServiceNames: ['Hair Cut & Styling', 'Facial Treatment'],
        comboServiceDetails: [
          {
            id: 'service-1',
            name: 'Hair Cut & Styling',
            serviceName: 'Hair Cut & Styling',
            duration: 60,
            price: 50,
            color: '#ff9800',
            image: '',
            category: 'Hair'
          },
          {
            id: 'service-2',
            name: 'Facial Treatment',
            serviceName: 'Facial Treatment',
            duration: 60,
            price: 45,
            color: '#ff9800',
            image: '',
            category: 'Beauty'
          }
        ]
      },
      {
        _id: 'service-4',
        name: 'Premium Beauty Package',
        description: 'Complete beauty treatment package',
        price: 150,
        duration: 180,
        image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=200&h=200&fit=crop',
        mode: 'inSalon',
        category: 'Package',
        subcategory: 'Premium',
        rating: 5.0,
        gender: 'Female',
        isCombo: true,
        offerType: 'month',
        selectedServices: ['Facial Treatment', 'Hair Cut & Styling', 'Manicure'],
        comboServiceNames: ['Facial Treatment', 'Hair Cut & Styling', 'Manicure'],
        comboServiceDetails: [
          {
            id: 'service-2',
            name: 'Facial Treatment',
            serviceName: 'Facial Treatment',
            duration: 60,
            price: 45,
            color: '#ff9800',
            image: '',
            category: 'Beauty'
          },
          {
            id: 'service-1',
            name: 'Hair Cut & Styling',
            serviceName: 'Hair Cut & Styling',
            duration: 60,
            price: 50,
            color: '#ff9800',
            image: '',
            category: 'Hair'
          },
          {
            id: 'service-5',
            name: 'Manicure',
            serviceName: 'Manicure',
            duration: 60,
            price: 55,
            color: '#ff9800',
            image: '',
            category: 'Nails'
          }
        ]
      }
    ],
    reviews: [
      {
        _id: 'review-1',
        userName: 'Sarah Johnson',
        rating: 5,
        comment: 'Amazing service! The stylist was very professional and my hair looks fantastic.',
        date: '2024-01-15',
        serviceName: 'Hair Cut & Styling'
      },
      {
        _id: 'review-2',
        userName: 'Maria Garcia',
        rating: 4,
        comment: 'Great facial treatment. Very relaxing and my skin feels refreshed.',
        date: '2024-01-10',
        serviceName: 'Facial Treatment'
      }
    ],
    photoGallery: [
      {
        _id: 'gallery-1',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=800&fit=crop',
        caption: 'Professional hair styling transformation',
        timestamp: '2024-01-20T10:30:00Z'
      },
      {
        _id: 'gallery-2',
        type: 'video',
        url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&h=800&fit=crop',
        caption: 'Hair coloring process',
        timestamp: '2024-01-19T15:45:00Z'
      },
      {
        _id: 'gallery-3',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600&h=800&fit=crop',
        caption: 'Facial treatment results',
        timestamp: '2024-01-18T14:20:00Z'
      },
      {
        _id: 'gallery-4',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=600&h=800&fit=crop',
        caption: 'Bridal makeup transformation',
        timestamp: '2024-01-17T12:15:00Z'
      },
      {
        _id: 'gallery-5',
        type: 'video',
        url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_2mb.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&h=800&fit=crop',
        caption: 'Hair cutting technique',
        timestamp: '2024-01-16T09:30:00Z'
      }
    ]
  },
  'pro-1': {
    _id: 'pro-1',
    name: 'Sarah Johnson',
    type: 'Professional',
    image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=300&fit=crop',
    images: [
      'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=300&fit=crop'
    ],
    rating: 4.9,
    reviewCount: 156,
    bio: 'Professional makeup artist and hair stylist with 12 years of experience in bridal and special event styling.',
    experience: '12+',
    specializations: ['Makeup', 'Hair Styling', 'Bridal Services'],
    address: '789 Style Lane, Midtown',
    phone: '+1 (555) 456-7890',
    email: 'sarah@beautypro.com',
    workingHours: 'Mon-Sun: 9AM-8PM',
    isVerified: true,
    totalBookings: 800,
    averageRating: 4.9,
    distance: 1.2,
    inSalon: false,
    atHome: true,
    categories: ['Makeup', 'Hair', 'Bridal'],
    minimum_order_value: 150,
    services: [
      {
        _id: 'service-3',
        name: 'Bridal Makeup',
        description: 'Complete bridal makeup with trial session',
        price: 200,
        duration: 120,
        image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop',
        mode: 'atHome',
        category: 'Makeup',
        subcategory: 'Bridal',
        rating: 4.9,
        gender: 'Female'
      },
      {
        _id: 'service-4',
        name: 'Hair Styling',
        description: 'Professional hair styling for events',
        price: 120,
        duration: 90,
        image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=200&h=200&fit=crop',
        mode: 'atHome',
        category: 'Hair',
        subcategory: 'Styling',
        rating: 4.8,
        gender: 'Both'
      }
    ],
    reviews: [
      {
        _id: 'review-3',
        userName: 'Emma Wilson',
        rating: 5,
        comment: 'Sarah did an amazing job on my wedding makeup! I felt beautiful all day.',
        date: '2024-01-08',
        serviceName: 'Bridal Makeup'
      },
      {
        _id: 'review-4',
        userName: 'Sophie Brown',
        rating: 5,
        comment: 'Professional and talented! My hair looked perfect for the party.',
        date: '2024-01-05',
        serviceName: 'Hair Styling'
      }
    ],
    photoGallery: [
      {
        _id: 'gallery-6',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=600&h=800&fit=crop',
        caption: 'Bridal makeup transformation',
        timestamp: '2024-01-20T11:00:00Z'
      },
      {
        _id: 'gallery-7',
        type: 'video',
        url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=800&fit=crop',
        caption: 'Hair styling process',
        timestamp: '2024-01-19T16:30:00Z'
      },
      {
        _id: 'gallery-8',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=800&fit=crop',
        caption: 'Event makeup look',
        timestamp: '2024-01-18T13:45:00Z'
      },
      {
        _id: 'gallery-9',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&h=800&fit=crop',
        caption: 'Professional hair styling',
        timestamp: '2024-01-17T10:20:00Z'
      }
    ]
  },
  'pros-1': {
    _id: 'pros-1',
    name: 'Sarah Johnson',
    type: 'Pros',
    image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=300&fit=crop',
    images: [
      'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=300&fit=crop'
    ],
    rating: 4.9,
    reviewCount: 89,
    bio: 'Professional makeup artist and hair stylist with 8 years of experience in bridal and event makeup.',
    experience: '8+',
    specializations: ['Bridal Makeup', 'Hair Styling', 'Event Makeup'],
    address: '456 Beauty Lane, Uptown',
    phone: '+1 (555) 987-6543',
    email: 'sarah.johnson@email.com',
    workingHours: 'Mon-Sun: 9AM-9PM',
    isVerified: true,
    totalBookings: 850,
    averageRating: 4.9,
    distance: 1.8,
    inSalon: false,
    atHome: true,
    categories: ['Makeup', 'Hair', 'Bridal'],
    minimum_order_value: 150,
    services: [
      {
        _id: 'service-5',
        name: 'Bridal Makeup',
        description: 'Complete bridal makeup with trial session',
        price: 200,
        duration: 120,
        image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200&h=200&fit=crop',
        mode: 'atHome',
        category: 'Makeup',
        subcategory: 'Bridal',
        rating: 4.9,
        gender: 'Female'
      },
      {
        _id: 'service-6',
        name: 'Hair Styling',
        description: 'Professional hair styling for events',
        price: 120,
        duration: 90,
        image: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=200&h=200&fit=crop',
        mode: 'atHome',
        category: 'Hair',
        subcategory: 'Styling',
        rating: 4.8,
        gender: 'Both'
      },
      {
        _id: 'service-7',
        name: 'Party Makeup',
        description: 'Glamorous party makeup with hair styling',
        price: 150,
        duration: 100,
        image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=200&h=200&fit=crop',
        mode: 'atHome',
        category: 'Makeup',
        subcategory: 'Party',
        rating: 4.7,
        gender: 'Female'
      }
    ],
    reviews: [
      {
        _id: 'review-5',
        userName: 'Emma Wilson',
        rating: 5,
        comment: 'Sarah did an amazing job on my wedding makeup! I felt beautiful all day.',
        date: '2024-01-08',
        serviceName: 'Bridal Makeup'
      },
      {
        _id: 'review-6',
        userName: 'Sophie Brown',
        rating: 5,
        comment: 'Professional and talented! My hair looked perfect for the party.',
        date: '2024-01-05',
        serviceName: 'Hair Styling'
      }
    ],
    photoGallery: [
      {
        _id: 'gallery-10',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=600&h=800&fit=crop',
        caption: 'Bridal makeup transformation',
        timestamp: '2024-01-20T11:00:00Z'
      },
      {
        _id: 'gallery-11',
        type: 'video',
        url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
        thumbnail: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=800&fit=crop',
        caption: 'Hair styling process',
        timestamp: '2024-01-19T16:30:00Z'
      },
      {
        _id: 'gallery-12',
        type: 'image',
        url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=800&fit=crop',
        caption: 'Event makeup look',
        timestamp: '2024-01-18T13:45:00Z'
      }
    ]
  }
};

const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { 
    isMobile: isResponsiveMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  
  // Get distance from navigation state
  const distanceFromState = location.state?.distance;
  
  // State management
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const { addToCart, isInCart, getItemQuantity, updateQuantity, removeFromCart, getItemsByProvider, items, updateItemQuantity } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState('All');
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [sortBy, setSortBy] = useState('category');
  const [categories, setCategories] = useState([]);
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // Debug function to copy console logs
  const copyConsoleLogs = () => {
    const logs = [];
    
    // Get provider and services debug info
    logs.push('=== PROVIDER PROFILE DEBUG INFO ===');
    logs.push(`Provider ID: ${id}`);
    logs.push(`Provider Data: ${JSON.stringify(provider, null, 2)}`);
    logs.push(`Services Count: ${services.length}`);
    
    // Get combo services specifically
    const comboServices = services.filter(service => service.isCombo);
    logs.push(`\n=== COMBO SERVICES ===`);
    logs.push(`Combo Services Count: ${comboServices.length}`);
    comboServices.forEach((service, index) => {
      logs.push(`\nCombo Service ${index + 1}:`);
      logs.push(`  Name: ${service.name}`);
      logs.push(`  isCombo: ${service.isCombo}`);
      logs.push(`  comboName: ${service.comboName}`);
      logs.push(`  comboId: ${service.comboId}`);
      logs.push(`  selectedServices: ${JSON.stringify(service.selectedServices)}`);
      logs.push(`  comboServiceNames: ${JSON.stringify(service.comboServiceNames)}`);
      logs.push(`  comboServiceDetails: ${JSON.stringify(service.comboServiceDetails)}`);
    });
    
    const logText = logs.join('\n');
    navigator.clipboard.writeText(logText).then(() => {
      alert('Console logs copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy logs. Please check console manually.');
    });
  };
  
  // Debug function to test combo add to cart
  const testComboAddToCart = () => {
    const comboServices = services.filter(service => service.isCombo);
    if (comboServices.length === 0) {
      alert('No combo services found!');
      return;
    }
    
    const comboService = comboServices[0];
    //console.log('🧪 [TEST] Testing combo add to cart with service:', comboService);
    
    // Simulate the add to cart process
    const comboServicesArray = comboService.selectedServices || comboService.services || comboService.includedServices || [];
  //  console.log('🧪 [TEST] Combo services array:', comboServicesArray);
    
    const cartItem = {
      id: comboService._id,
      _id: comboService._id,
      name: comboService.name,
      price: comboService.price,
      image: comboService.image,
      itemType: 'service',
      providerId: id,
      providerName: provider?.businessName || provider?.name || 'Provider',
      duration: comboService.duration,
      quantity: 1,
      isCombo: comboService.isCombo || false,
      comboName: comboService.isCombo ? comboService.name : null,
      comboId: comboService.isCombo ? comboService._id : null,
      selectedServices: comboServicesArray,
      comboServiceNames: comboServicesArray.map(serviceItem => {
        if (typeof serviceItem === 'string') {
          return serviceItem;
        } else {
          return serviceItem.name || serviceItem.serviceName || serviceItem;
        }
      }),
      comboServiceDetails: comboServicesArray.map(serviceItem => {
        if (typeof serviceItem === 'string') {
          const foundService = provider?.services?.find(s => s.name === serviceItem || s.serviceName === serviceItem);
          if (foundService) {
            return {
              id: foundService._id,
              name: foundService.name,
              serviceName: foundService.name,
              duration: foundService.duration,
              price: foundService.price,
              color: foundService.colorHex || foundService.serviceColor || '#ff9800',
              image: foundService.image || '',
              category: foundService.category || 'General'
            };
          }
        }
        return null;
      }).filter(Boolean)
    };
    
  //  console.log('🧪 [TEST] Final cart item:', cartItem);
    
    const logs = [];
    logs.push('=== COMBO ADD TO CART TEST ===');
    logs.push(`Original Service: ${JSON.stringify(comboService, null, 2)}`);
    logs.push(`\nCart Item: ${JSON.stringify(cartItem, null, 2)}`);
    
    const logText = logs.join('\n');
    navigator.clipboard.writeText(logText).then(() => {
      alert('Combo test data copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy test data. Please check console manually.');
    });
  };

  // Load provider data
  useEffect(() => {
    const loadProvider = async () => {
      setLoading(true);
      try {
        // Use the new combined API call
        const allDataRes = await providerAPI.getProviderAllData(id);
        
        // Extract data from the combined response
        // The backend returns data in { success, message, data, timestamp } format
        const providerData = allDataRes.data;
        
        setProvider(providerData);
        setServices(providerData.services || []);
        setReviews(providerData.reviews || []);
        
        // DEBUG: Check if displayOrder is in the data
        console.log('🔍 [DISPLAY ORDER DEBUG] Received services from API:', (providerData.services || []).slice(0, 3).map(s => ({
          name: s.name,
          category: s.category,
          displayOrder: s.displayOrder
        })));
        
        // Extract categories from provider's services
        console.log('🔍 Extracting categories from provider services...');
        console.log('📊 Provider services sample:', (providerData.services || []).slice(0, 3).map(s => ({
          name: s.serviceName || s.name,
          category: s.category,
          categoryId: s.categoryId,
          categoryIdType: typeof s.categoryId
        })));
        
        // Collect all unique categories with their full data (if available)
        const categoryMap = new Map();
        
        (providerData.services || [])
          .filter(s => !s.isCombo && !s.offerType)
          .forEach(service => {
            let categoryData = null;
            
            // Check if categoryId is populated with full object (has iconUrl)
            if (service.categoryId && typeof service.categoryId === 'object' && service.categoryId.name) {
              categoryData = {
                name: service.categoryId.name,
                iconUrl: service.categoryId.iconUrl || null,
                icon: service.categoryId.icon || null,
                color: service.categoryId.color || null
              };
            }
            // Fallback to category string field
            else if (service.category && typeof service.category === 'string') {
              categoryData = { name: service.category };
            }
            // Check other possible field names
            else if (service.serviceCategory) {
              categoryData = { name: service.serviceCategory };
            }
            else if (service.categoryName) {
              categoryData = { name: service.categoryName };
            }
            
            // Add to map if valid and not already added
            if (categoryData && 
                categoryData.name && 
                typeof categoryData.name === 'string' &&
                categoryData.name.trim().length > 0 &&
                !['Offers', 'Combos', 'offers', 'combos', 'Services', 'services'].includes(categoryData.name.toLowerCase()) &&
                !categoryMap.has(categoryData.name)) {
              categoryMap.set(categoryData.name, categoryData);
            }
          });
        
        const categories = Array.from(categoryMap.values());
        
        console.log('✅ Provider categories:', categories);
        console.log('🎨 Categories with icons:', categories.filter(c => c.iconUrl).length);
        
        setCategories(categories);
      } catch (error) {
        console.error('❌ Error fetching provider data:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        setProvider(null);
        setServices([]);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadProvider();
    checkFavoriteStatus();
  }, [id]);


  const checkFavoriteStatus = async () => {
    try {
     // console.log('🔍 [FAVORITES] Checking favorite status for provider:', id);
      const response = await favoritesAPI.checkFavoriteStatus(id, 'provider');
    //  console.log('🔍 [FAVORITES] Favorite status response:', response);
      if (response.success) {
        setIsFavorite(response.data.isFavorite);
        setFavoriteId(response.data.favoriteId);
     //   console.log('🔍 [FAVORITES] Set favorite state:', response.data.isFavorite, 'ID:', response.data.favoriteId);
      }
    } catch (error) {
      console.error('❌ [FAVORITES] Error checking favorite status:', error);
    }
  };

  // Auto-scroll gallery effect
  useEffect(() => {
    if (!provider?.photoGallery || provider.photoGallery.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === provider.photoGallery.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [provider?.photoGallery]);

  // Add CSS for gallery animation and story progress
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInOut {
        0%, 25% { opacity: 1; }
        33%, 100% { opacity: 0; }
      }
      
      @keyframes progress {
        0% { width: 0%; }
        100% { width: 100%; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const toggleFavorite = async () => {
    if (favoriteLoading) return;
    
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        // Remove from favorites
     //   console.log('🗑️ [FAVORITES] Removing from favorites, favoriteId:', favoriteId);
        if (favoriteId) {
          const response = await favoritesAPI.removeFromFavorites(favoriteId);
        //  console.log('🗑️ [FAVORITES] Remove response:', response);
          setIsFavorite(false);
          setFavoriteId(null);
        }
      } else {
        // Add to favorites
     //   console.log('➕ [FAVORITES] Adding to favorites, providerId:', id);
        const response = await favoritesAPI.addToFavorites(id, 'provider');
      //  console.log('➕ [FAVORITES] Add response:', response);
        if (response.success) {
          setIsFavorite(true);
          setFavoriteId(response.data.favorite.id);
        //  console.log('➕ [FAVORITES] Set favorite state: true, ID:', response.data.favorite.id);
        }
      }
    } catch (error) {
      console.error('❌ [FAVORITES] Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${provider.name} - NIStylez Provider`,
        text: `Check out ${provider.name} on NIStylez! ${provider.rating}★ rating with ${provider.reviewCount} reviews.`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleAddToCart = (service) => {
    try {
      // Transform service to match cart item format
      const cartItem = {
        id: service._id,
        _id: service._id,
        name: service.name,
        price: service.price,
        image: service.image,
        itemType: 'service',
        providerId: id, // Use provider ID for grouping
        providerName: provider?.businessName || provider?.name || 'Provider', // Include provider name
        duration: service.duration, // Include duration field
        quantity: 1
      };
      
      addToCart(cartItem);
      // Don't auto-open cart - user can click cart icon if needed
    } catch (error) {
      console.error('Error in handleAddToCart:', error);
    }
  };

  const updateCartItemQuantity = (serviceId, newQuantity) => {
    // Find the cart item to update
    const cartItem = items.find(item => 
      item.id === serviceId && 
      item.itemType === 'service' && 
      item.providerId === id
    );
    if (cartItem) {
      updateItemQuantity(cartItem, newQuantity);
    }
  };

  const calculateCartTotal = () => {
    const providerItems = getItemsByProvider(id);
    return providerItems.reduce((total, item) => {
      return total + (item.price * (item.quantity || 1));
    }, 0);
  };

  const getCartItemCount = () => {
    const providerItems = getItemsByProvider(id);
    return providerItems.reduce((count, item) => count + (item.quantity || 1), 0);
  };

  // Helper function to format duration
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    // If duration is already a string with "min" or similar, return as is
    if (typeof duration === 'string') {
      if (duration.includes('min') || duration.includes('hour') || duration.includes('hr')) {
        return duration;
      }
      // If it's just a number as string, add "min"
      const numDuration = parseInt(duration);
      if (!isNaN(numDuration)) {
        return `${numDuration} min`;
      }
      return duration;
    }
    
    // If duration is a number, add "min"
    if (typeof duration === 'number') {
      return `${duration} min`;
    }
    
    return 'N/A';
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star key={i} sx={{ 
          color: i <= rating ? '#ffd700' : '#ddd',
          fontSize: 16
        }} />
      );
    }
    return stars;
  };

  const getGenderIcon = (gender) => {
    // Return null if gender is null, undefined, or empty to avoid blank elements
    if (!gender || gender.trim() === '') {
      return null;
    }
    
    switch (gender) {
      case 'Male':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Male sx={{ fontSize: 16, color: '#1976d2' }} />
          </Box>
        );
      case 'Female':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Female sx={{ fontSize: 16, color: '#e91e63' }} />
          </Box>
        );
      case 'Both':
      case 'Unisex':
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Male sx={{ fontSize: 14, color: '#1976d2' }} />
            <Female sx={{ fontSize: 14, color: '#e91e63' }} />
          </Box>
        );
      default:
        // For unknown gender values, return null instead of default icons
        return null;
    }
  };


  // Story viewer functions
  const openStoryViewer = (index) => {
    setCurrentStoryIndex(index);
    setShowStoryViewer(true);
    setIsVideoPlaying(false);
  };

  const closeStoryViewer = () => {
    setShowStoryViewer(false);
    setIsVideoPlaying(false);
  };

  const nextStory = () => {
    if (provider?.photoGallery && currentStoryIndex < provider.photoGallery.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
      setIsVideoPlaying(false);
    }
  };

  const prevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
      setIsVideoPlaying(false);
    }
  };

  const toggleVideoPlay = () => {
    setIsVideoPlaying(!isVideoPlaying);
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <BrandLoader />
      </Box>
    );
  }

  if (!provider) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Provider not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>

      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        height: '60vh',
        background: provider?.photoGallery && provider.photoGallery.length > 0 ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        overflow: 'hidden',
        '&::before': {
          content: provider?.photoGallery && provider.photoGallery.length > 0 ? 'none' : '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        },
        '&::after': {
          content: provider?.photoGallery && provider.photoGallery.length > 0 ? 'none' : '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100px',
          background: 'linear-gradient(to top, rgba(255,255,255,0.1), transparent)'
        }
      }}>
        {/* Auto-scrolling Photo Gallery */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          overflow: 'hidden',
          zIndex: 1
        }}>
          {/* Photo Gallery */}
          {provider.photoGallery && provider.photoGallery.length > 0 ? (
            <>
              {/* Test: Always show first image to debug */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2
                }}
              >
                <Box
                  component="img"
                  src={provider.photoGallery[0].url}
                  alt={provider.photoGallery[0].caption || 'Gallery image'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'brightness(0.7)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
              
              {/* Dynamic gallery with opacity transitions */}
              {provider.photoGallery.map((item, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: index === currentImageIndex ? 1 : 0,
                  transition: 'opacity 1s ease-in-out',
                  zIndex: index === currentImageIndex ? 3 : 1
                }}
              >
                <Box
                  component="img"
                  src={item.url}
                  alt={item.caption || 'Gallery image'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'brightness(0.7)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
              ))}
            </>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
               background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
               backgroundPosition: 'center',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center'
             }}
           >
             <Box sx={{ textAlign: 'center', color: 'white' }}>
               <PhotoLibrary sx={{ fontSize: 80, mb: 2, opacity: 0.7 }} />
               <Typography variant="h5" sx={{ fontWeight: 'bold', opacity: 0.9 }}>
                 {provider.name}
               </Typography>
               <Typography variant="body1" sx={{ opacity: 0.8 }}>
                 Professional Beauty Services
               </Typography>
             </Box>
           </Box>
          )}
        </Box>
        
        {/* Gallery Indicators */}
        {provider.photoGallery && provider.photoGallery.length > 1 && (
          <Box sx={{ 
            position: 'absolute', 
            bottom: 20, 
            left: '50%', 
            transform: 'translateX(-50%)',
            display: 'flex', 
            gap: 1,
            zIndex: 3
          }}>
            {provider.photoGallery.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                   backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                   transition: 'background-color 0.3s ease',
                   cursor: 'pointer'
                }}
                 onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </Box>
        )}
         
         {/* Navigation Arrows */}
         {provider.photoGallery && provider.photoGallery.length > 1 && (
           <>
             {/* Previous Button */}
             <IconButton
               onClick={() => setCurrentImageIndex(prev => 
                 prev === 0 ? provider.photoGallery.length - 1 : prev - 1
               )}
               sx={{
                 position: 'absolute',
                 left: { xs: 10, md: 20 },
                 top: '50%',
                 transform: 'translateY(-50%)',
                 bgcolor: 'rgba(0,0,0,0.5)',
                 color: 'white',
                 zIndex: 3,
                 width: { xs: 36, md: 48 },
                 height: { xs: 36, md: 48 },
                 '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
               }}
             >
               <NavigateBefore sx={{ fontSize: { xs: 20, md: 24 } }} />
             </IconButton>
             
             {/* Next Button */}
             <IconButton
               onClick={() => setCurrentImageIndex(prev => 
                 prev === provider.photoGallery.length - 1 ? 0 : prev + 1
               )}
               sx={{
                 position: 'absolute',
                 right: { xs: 10, md: 20 },
                 top: '50%',
                 transform: 'translateY(-50%)',
                 bgcolor: 'rgba(0,0,0,0.5)',
                 color: 'white',
                 zIndex: 3,
                 width: { xs: 36, md: 48 },
                 height: { xs: 36, md: 48 },
                 '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
               }}
             >
               <NavigateNext sx={{ fontSize: { xs: 20, md: 24 } }} />
             </IconButton>
           </>
         )}
        
        {/* Navigation Bar */}
        <AppBar position="absolute" sx={{ background: 'transparent', boxShadow: 'none', zIndex: 4 }}>
          <Toolbar sx={{ minHeight: { xs: 48, md: 64 }, px: { xs: 1, md: 2 } }}>
            <IconButton 
              onClick={() => {
                // Check if there's history to go back to
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  // If no history (direct link), go to home
                  navigate('/');
                }
              }}
              sx={{ color: 'white', mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 } }}>
              <IconButton 
                onClick={toggleFavorite} 
                sx={{ color: isFavorite ? '#ff6b6b' : 'white' }} 
                disabled={favoriteLoading}
                size={isMobile ? "small" : "medium"}
              >
              {favoriteLoading ? <BrandLoader size="small" showText={false} /> : (isFavorite ? <Favorite /> : <FavoriteBorder />)}
            </IconButton>
              <IconButton 
                onClick={handleShare} 
                sx={{ color: 'white' }}
                size={isMobile ? "small" : "medium"}
              >
              <Share />
            </IconButton>
            <Tooltip title={`${provider.name} (${getCartItemCount()} items)`} arrow>
                <IconButton 
                  onClick={() => window.dispatchEvent(new CustomEvent('openCart'))} 
                  sx={{ color: 'white' }}
                  size={isMobile ? "small" : "medium"}
                >
                <Badge badgeContent={getCartItemCount()} color="error">
                  <ShoppingCart />
                </Badge>
              </IconButton>
            </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Hero Content */}
        <Box sx={{ textAlign: 'center', zIndex: 2, mt: 8 }}>
          {/* Removed profile icon and name from here - moved to profile info box */}
        </Box>
      </Box>

      {/* Profile Info Box */}
      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 1.5, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: -4, position: 'relative', zIndex: 3 
        }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 25px rgba(0,0,0,0.1)', mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {/* Profile Icon and Name - Side by Side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
              
              <Avatar 
                src={provider.photoURL || provider.profilePhotoUrl || provider.profilePicture || provider.logoUrl || provider.businessImage || provider.image || ''} 
                sx={{ 
                  width: { xs: 60, md: 80 }, 
                  height: { xs: 60, md: 80 },
                  border: '3px solid #e0e0e0',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}
              >
                {/* Fallback: Show first letter of business name if no image */}
                {!provider.photoURL && !provider.profilePhotoUrl && !provider.profilePicture && !provider.logoUrl && !provider.businessImage && !provider.image && provider.name ? 
                  provider.name.charAt(0).toUpperCase() : null
                }
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold', mb: 0.5, wordBreak: 'break-word' }}>
                  {provider.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                    {provider.type === 'Store' ? (provider.providerType === 'salon' ? 'Salon' : 'Pros') : provider.type} • 📍 {distanceFromState || provider.distance} away
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton 
                      onClick={() => {
                        const phoneNumber = provider.phone || provider.contactNumber || provider.phoneNumber;
                        if (phoneNumber) {
                          const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
                          window.open(`tel:${cleanNumber}`, '_self');
                        } else {
                          alert('Phone number not available for this provider');
                        }
                      }}
                      size="small"
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white',
                        width: { xs: 24, sm: 26 },
                        height: { xs: 24, sm: 26 },
                        '&:hover': { 
                          bgcolor: 'primary.dark'
                        }
                      }}
                    >
                      <Phone sx={{ fontSize: { xs: 13, sm: 14 } }} />
                    </IconButton>
                    <IconButton 
                      onClick={() => {
                        navigate(`/chat/${id}`, {
                          state: {
                            provider: {
                              id: id,
                              name: provider?.businessName || provider?.name || 'Provider',
                              avatar: provider?.logoUrl || provider?.image,
                              businessType: provider?.type || provider?.providerType
                            }
                          }
                        });
                      }}
                      size="small"
                      sx={{ 
                        bgcolor: 'secondary.main', 
                        color: 'white',
                        width: { xs: 24, sm: 26 },
                        height: { xs: 24, sm: 26 },
                        '&:hover': { 
                          bgcolor: 'secondary.dark'
                        }
                      }}
                    >
                      <Chat sx={{ fontSize: { xs: 13, sm: 14 } }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Rating and Actions - Mobile Optimized */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              alignItems: { xs: 'stretch', sm: 'center' }, 
              justifyContent: 'space-between', 
              gap: { xs: 2, sm: 1 },
              mb: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    cursor: 'pointer',
                    padding: { xs: 0.5, sm: 1 },
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => navigate(`/provider/${id}/reviews`)}
                >
                  {renderStars(provider.rating || 5)}
                  <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 'bold' }}>
                    {(() => {
                      const rating = provider.rating || 5;
                      return typeof rating === 'number' ? rating.toFixed(1) : rating;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    ({provider.reviewCount || 0} reviews)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                   icon={provider.type === 'Salon' || provider.providerType === 'salon' ? <Business /> : <Person />}
                   label={provider.type === 'Store' ? (provider.providerType === 'salon' ? 'Salon' : 'Pros') : provider.type} 
                  color="primary"
                  variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  sx={{ fontWeight: 'bold' }}
                />
                {provider.isVerified && (
                  <Chip 
                    icon={<CheckCircle />}
                    label="Verified" 
                    color="success"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Enhanced Main Content */}
      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 1, md: 3 },
          width: '100%',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
          position: 'relative', 
          zIndex: 3,
          minHeight: '100vh'
        }}>
        <ProfileCard sx={{ width: '100%' }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            {/* Enhanced Quick Stats */}
            <Box sx={{ 
              mb: 4,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: 3,
              p: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              border: '1px solid #e8eaf6'
            }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ 
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)'
                  }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                                             {provider.totalBookings || 50}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total Bookings
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ 
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)'
                  }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {distanceFromState || provider.distance}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Distance Away
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Enhanced Tab Navigation */}
            <Box sx={{ 
              mb: 4,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
              borderRadius: 3,
              p: { xs: 0.5, sm: 1 },
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e8eaf6'
            }}>
              <Tabs 
                value={selectedTab} 
                onChange={handleTabChange} 
                variant="fullWidth"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  width: '100%',
                  '& .MuiTab-root': {
                    minHeight: { xs: 56, sm: 64 },
                    borderRadius: 2,
                    margin: { xs: '0 1px', sm: '0 2px' },
                    transition: 'all 0.3s ease',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.95rem' },
                    color: '#666',
                    minWidth: 0,
                    flex: 1,
                    padding: { xs: '8px 2px', sm: '12px 8px' },
                    '&:hover': {
                      background: 'rgba(102, 126, 234, 0.08)',
                      transform: 'translateY(-2px)',
                      color: '#667eea'
                    }
                  },
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white !important',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                    '& .MuiSvgIcon-root': {
                      color: 'white'
                    }
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#667eea',
                    '&.Mui-disabled': {
                      opacity: 0.3
                    }
                  }
                }}
              >
                <Tab 
                  icon={<LocalOffer sx={{ fontSize: { xs: 18, sm: 20 }, mb: 0.5 }} />} 
                  label="Services" 
                  iconPosition="top"
                />
                <Tab 
                  icon={<RateReview sx={{ fontSize: { xs: 18, sm: 20 }, mb: 0.5 }} />} 
                  label="Reviews" 
                  iconPosition="top"
                />
                <Tab 
                  icon={<PhotoLibrary sx={{ fontSize: { xs: 18, sm: 20 }, mb: 0.5 }} />} 
                  label="Gallery" 
                  iconPosition="top"
                />
                <Tab 
                  icon={<Business sx={{ fontSize: { xs: 18, sm: 20 }, mb: 0.5 }} />} 
                  label="About us" 
                  iconPosition="top"
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ mt: 3 }}>
              {selectedTab === 0 && (
                <Box>
                  {/* Category Filter Cards with Icons */}
                  {categories.length > 0 && (
                  <Box sx={{ 
                      mb: 1,
                    display: 'flex', 
                      gap: { xs: 1.5, sm: 2 },
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 3 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: 3 },
                      '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' }
                    }}>
                      {categories.map((category, index) => {
                        const isSelected = categoryFilters.includes(category.name);
                        console.log(`🎨 Rendering category "${category.name}":`, {
                          iconUrl: category.iconUrl,
                          icon: category.icon,
                          hasIconUrl: !!category.iconUrl,
                          willShowImage: !!category.iconUrl
                        });
                        return (
                          <Card
                            key={index}
                            onClick={() => {
                              if (isSelected) {
                                setCategoryFilters(prev => prev.filter(c => c !== category.name));
                              } else {
                                setCategoryFilters(prev => [...prev, category.name]);
                              }
                            }}
                            sx={{
                              minWidth: { xs: 90, sm: 100 },
                              maxWidth: { xs: 90, sm: 100 },
                              height: { xs: 100, sm: 110 },
                              background: isSelected 
                                ? 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)'
                                : 'linear-gradient(180deg, rgba(230, 242, 255, 0.4) 0%, rgba(200, 228, 255, 0.6) 18%, #2874BA 100%)',
                              borderRadius: { xs: '0 25px 0 0', sm: '0 30px 0 0' },
                              padding: { xs: '8px 8px 12px', sm: '10px 10px 14px' },
                              position: 'relative',
                              boxShadow: isSelected 
                                ? '0 4px 16px rgba(255, 215, 0, 0.4)'
                                : '0 3px 12px rgba(40, 116, 186, 0.18)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              flexShrink: 0,
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: isSelected
                                  ? '0 6px 20px rgba(255, 215, 0, 0.5)'
                                  : '0 6px 20px rgba(40, 116, 186, 0.3)'
                              }
                            }}
                          >
                            {/* Checkmark - Top Right Corner - ONLY when selected */}
                            {isSelected && (
                              <Box sx={{ 
                                position: 'absolute',
                                top: { xs: 4, sm: 6 },
                                right: { xs: 4, sm: 6 },
                                backgroundColor: '#1a237e',
                                borderRadius: '50%',
                                width: { xs: 20, sm: 22 },
                                height: { xs: 20, sm: 22 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                zIndex: 10
                              }}>
                                <CheckCircle sx={{ color: 'white', fontSize: { xs: 12, sm: 14 } }} />
                              </Box>
                            )}
                            
                  {/* Category Name - Centered */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 700,
                      color: isSelected ? '#1a237e' : 'white',
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textShadow: isSelected ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.15)',
                      lineHeight: 1.2,
                      px: 1
                    }}
                  >
                    {category.name}
                  </Typography>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                  
                  {/* Search Toggle Button */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    alignItems: 'center', 
                    mb: 1
                  }}>
                    <IconButton
                      onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                      sx={{
                        bgcolor: isSearchExpanded ? '#667eea' : '#f8f9fa',
                        color: isSearchExpanded ? 'white' : '#667eea',
                        width: { xs: 40, md: 48 },
                        height: { xs: 40, md: 48 },
                        boxShadow: isSearchExpanded ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: isSearchExpanded ? '#5568d3' : '#e8eaf6',
                          transform: 'scale(1.05)',
                          boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                        }
                      }}
                    >
                      {isSearchExpanded ? <Close sx={{ fontSize: { xs: 20, md: 24 } }} /> : <Search sx={{ fontSize: { xs: 20, md: 24 } }} />}
                    </IconButton>
                  </Box>
                  
                  {/* Enhanced Search and Filter Section - Collapsible */}
                  <Box sx={{ 
                    mb: 1,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: 3,
                    boxShadow: isSearchExpanded ? '0 4px 20px rgba(0,0,0,0.06)' : 'none',
                    border: isSearchExpanded ? '1px solid #e8eaf6' : 'none',
                    width: '100%',
                    mx: { xs: 0, md: 0 },
                    maxHeight: isSearchExpanded ? '1000px' : '0px',
                    overflow: 'hidden',
                    opacity: isSearchExpanded ? 1 : 0,
                    p: isSearchExpanded ? { xs: 2, md: 3 } : 0,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}>
                    {/* Enhanced Search Bar */}
                    <TextField
                      fullWidth
                      placeholder="Search services..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: '#667eea' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchQuery('')}
                              sx={{ 
                                color: '#667eea',
                                '&:hover': { 
                                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                                  color: '#5568d3'
                                }
                              }}
                            >
                              <Close />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: '#f8f9fa',
                          '&:hover': {
                            background: '#f1f3f4'
                          },
                          '&.Mui-focused': {
                            background: 'white',
                            boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)'
                          }
                        }
                      }}
                    />

                    {/* Enhanced Filters and Sorting */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {/* Gender Filter */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: '#667eea', fontWeight: 500 }}>Gender</InputLabel>
                          <Select
                            value={genderFilter}
                            onChange={(e) => setGenderFilter(e.target.value)}
                            label="Gender"
                            sx={{
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e8eaf6'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#667eea'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#667eea',
                                borderWidth: 2
                              }
                            }}
                          >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                            <MenuItem value="Both">Unisex</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Category Filter - Synced with category cards above */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: '#667eea', fontWeight: 500 }}>Category</InputLabel>
                          <Select
                            multiple
                            value={categoryFilters}
                            onChange={(e) => setCategoryFilters(e.target.value)}
                            label="Category"
                            renderValue={(selected) => selected.length > 0 ? selected.join(', ') : 'All'}
                            sx={{
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e8eaf6'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#667eea'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#667eea',
                                borderWidth: 2
                              }
                            }}
                          >
                            {categories.map((category) => (
                              <MenuItem key={category.name} value={category.name}>
                                <Checkbox 
                                  checked={categoryFilters.indexOf(category.name) > -1}
                                  sx={{ 
                                    color: '#667eea',
                                    '&.Mui-checked': {
                                      color: '#FFD700'
                                    }
                                  }}
                                />
                                <ListItemText primary={category.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Sort By */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Sort By</InputLabel>
                          <Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            label="Sort By"
                          >
                            <MenuItem value="category">Category</MenuItem>
                            <MenuItem value="name">Name</MenuItem>
                            <MenuItem value="price">Price</MenuItem>
                            <MenuItem value="rating">Rating</MenuItem>
                            <MenuItem value="duration">Duration</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Sort Order */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Order</InputLabel>
                          <Select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            label="Order"
                          >
                            <MenuItem value="asc">Low to High</MenuItem>
                            <MenuItem value="desc">High to Low</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Clear Filters Button */}
                    {(genderFilter !== 'All' || categoryFilters.length > 0 || searchQuery !== '') && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setGenderFilter('All');
                          setCategoryFilters([]);
                          setSearchQuery('');
                        }}
                        sx={{ mb: 2 }}
                      >
                        Clear All Filters
                      </Button>
                    )}
                  </Box>

                  {/* Services Section */}
                  {(() => {
                    const regularServices = provider.services.filter(s => !s.isCombo && !s.offerType);
                    console.log('🔍 Services Debug:', {
                      totalServices: provider.services?.length || 0,
                      regularServices: regularServices.length,
                      allServices: provider.services?.map(s => ({
                        name: s.serviceName || s.name,
                        isFeatured: s.isFeatured,
                        isCombo: s.isCombo,
                        offerType: s.offerType
                      })) || []
                    });
                    return regularServices.length > 0;
                  })() && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Build sx={{ color: '#1976d2' }} />
                        Services
                      </Typography>
                      
                      {/* Group by Category or show as flat list */}
                      {sortBy === 'category' ? (
                        // GROUP BY CATEGORY VIEW
                        <>
                          {(() => {
                            // Filter services first
                            const filteredServices = provider.services.filter(service => {
                              const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                service.description.toLowerCase().includes(searchQuery.toLowerCase());
                              const matchesGender = genderFilter === 'All' || service.gender === genderFilter;
                              
                              // Check category match - handle both categoryId.name and category string
                              let serviceCategoryName = null;
                              if (service.categoryId && typeof service.categoryId === 'object' && service.categoryId.name) {
                                serviceCategoryName = service.categoryId.name;
                              } else if (service.category) {
                                serviceCategoryName = service.category;
                              }
                              
                              const matchesCategory = categoryFilters.length === 0 || 
                                (serviceCategoryName && categoryFilters.includes(serviceCategoryName));
                              
                              return matchesSearch && matchesGender && matchesCategory && !service.isCombo && !service.offerType;
                            });

                            // Group by category
                            const servicesByCategory = filteredServices.reduce((acc, service) => {
                              // Use same category name extraction logic
                              let serviceCategoryName = 'Uncategorized';
                              if (service.categoryId && typeof service.categoryId === 'object' && service.categoryId.name) {
                                serviceCategoryName = service.categoryId.name;
                              } else if (service.category) {
                                serviceCategoryName = service.category;
                              }
                              
                              if (!acc[serviceCategoryName]) {
                                acc[serviceCategoryName] = [];
                              }
                              acc[serviceCategoryName].push(service);
                              return acc;
                            }, {});
                            
                            // DEBUG: Log services with displayOrder
                            console.log('🔍 [DISPLAY ORDER DEBUG] Services by category:', Object.keys(servicesByCategory).map(cat => ({
                              category: cat,
                              services: servicesByCategory[cat].map(s => ({
                                name: s.name,
                                displayOrder: s.displayOrder
                              }))
                            })));

                            // Sort categories alphabetically
                            const sortedCategories = Object.keys(servicesByCategory).sort((a, b) => {
                              if (sortOrder === 'asc') {
                                return a.toLowerCase() > b.toLowerCase() ? 1 : -1;
                              } else {
                                return a.toLowerCase() < b.toLowerCase() ? 1 : -1;
                              }
                            });

                            return sortedCategories.map(category => (
                              <Box key={category} sx={{ mb: 4 }}>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 'bold', 
                                    mb: 2, 
                                    color: '#667eea',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    borderBottom: '2px solid #667eea',
                                    pb: 1
                                  }}
                                >
                                  {category}
                                  <Chip 
                                    label={servicesByCategory[category].length} 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: '#667eea', 
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem'
                                    }} 
                                  />
                                </Typography>
                                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mx: 0 }}>
                                  {(() => {
                                    console.log(`🔍 [SORTING DEBUG] Category: ${category}, Services:`, servicesByCategory[category].map(s => ({
                                      name: s.name,
                                      displayOrder: s.displayOrder,
                                      displayOrderType: typeof s.displayOrder
                                    })));
                                    return servicesByCategory[category]
                                      .sort((a, b) => {
                                        // Sort by displayOrder (access it the same way as price, name, etc.)
                                        const aOrder = a.displayOrder;
                                        const bOrder = b.displayOrder;
                                        
                                        // Sort by displayOrder first (if both have it)
                                        if (aOrder !== undefined && aOrder !== null && bOrder !== undefined && bOrder !== null) {
                                          console.log(`🔢 Comparing ${a.name}(displayOrder: ${aOrder}) vs ${b.name}(displayOrder: ${bOrder}) = ${Number(aOrder) - Number(bOrder)}`);
                                          return Number(aOrder) - Number(bOrder);
                                        }
                                        
                                        // Services with displayOrder come before those without
                                        if (aOrder !== undefined && aOrder !== null) return -1;
                                        if (bOrder !== undefined && bOrder !== null) return 1;
                                        
                                        // If both have no displayOrder, sort by name alphabetically
                                        return a.name.localeCompare(b.name);
                                      })
                                      .map((service) => {
                        const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                        return (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={service._id}>
                            <ServiceCard sx={{ 
                              width: '100%', 
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              {/* Image Section */}
                              <Box 
                                sx={{ position: 'relative' }}
                                      onClick={() => navigate(`/service/${service._id}/${id}`)}
                                    >
                                <CardMedia
                                  component="img"
                                  image={service.image || '/placeholder-service.jpg'}
                                        alt={service.name}
                                  sx={{
                                          objectFit: 'cover',
                                    width: '100%',
                                    height: { xs: 140, md: 160 },
                                    cursor: 'pointer'
                                        }}
                                        onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                
                                {/* Featured Badge */}
                                {service.isFeatured && (
                                  <Chip
                                    label="Featured"
                                    sx={{
                                      position: 'absolute',
                                      top: 6,
                                      left: 6,
                                      backgroundColor: '#ffd700',
                                      color: '#333',
                                      fontWeight: 'bold',
                                      fontSize: '0.5rem',
                                      height: 18,
                                      '& .MuiChip-label': {
                                        fontSize: '0.5rem',
                                        px: 0.5
                                      }
                                    }}
                                  />
                                )}
                                    </Box>

                              {/* Content Section */}
                              <CardContent sx={{ 
                                flexGrow: 1, 
                                p: { xs: 1, md: 1.5 },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                              }}>
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        fontWeight: 'bold', 
                                        mb: 0.5, 
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        cursor: 'pointer',
                                        '&:hover': { color: 'primary.main' }
                                      }}
                                      onClick={() => navigate(`/service/${service._id}/${id}`)}
                                    >
                                      {service.name}
                                    </Typography>
                                
                                    <Typography 
                                      variant="body2" 
                                  color="text.secondary" 
                                  sx={{ mb: 0.5, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                                    >
                                      by {provider?.businessName || provider?.name || 'Provider'}
                                    </Typography>

                                {/* Price and Duration */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                        ${service.price}
                                      </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                        <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                                          {formatDuration(service.duration)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                
                                {/* Category and Gender */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Chip 
                                          label={service.category} 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ 
                                      fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                                      height: 18,
                                            '& .MuiChip-label': {
                                        px: 0.5
                                            }
                                          }}
                                        />
                                            {getGenderIcon(service.gender)}
                                    </Box>
                                    
                                {/* Cart Controls */}
                                    {cartItem ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton 
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateCartItemQuantity(service._id, cartItem.quantity - 1);
                                          }}
                                          sx={{ 
                                        bgcolor: '#f5f5f5',
                                        border: '1px solid #ddd',
                                        width: 24,
                                        height: 24,
                                        '&:hover': { bgcolor: '#e0e0e0' }
                                      }}
                                    >
                                      <Remove sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.8rem' }}>
                                          {cartItem.quantity}
                                        </Typography>
                                        <IconButton 
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateCartItemQuantity(service._id, cartItem.quantity + 1);
                                          }}
                                          sx={{ 
                                        bgcolor: '#1a237e',
                                            color: 'white',
                                        width: 24,
                                        height: 24,
                                        '&:hover': { bgcolor: '#0d47a1' }
                                      }}
                                    >
                                      <Add sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Box>
                                    ) : (
                                      <Button
                                        variant="contained"
                                    fullWidth
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddToCart(service);
                                        }}
                                        sx={{ 
                                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                                      minHeight: '32px',
                                      maxHeight: '32px',
                                          py: 0.5,
                                      bgcolor: '#1a237e',
                                      '&:hover': { bgcolor: '#0d47a1' },
                                      textTransform: 'none'
                                        }}
                                      >
                                        Add to Cart
                                      </Button>
                                    )}
                              </CardContent>
                            </ServiceCard>
                          </Grid>
                        );
                      });
                                  })()}
                                </Grid>
                              </Box>
                            ));
                          })()}
                        </>
                      ) : (
                        // FLAT LIST VIEW (for other sort options)
                        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%', mx: 0 }}>
                          {provider.services
                            .filter(service => {
                              // Search filter
                              const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                service.description.toLowerCase().includes(searchQuery.toLowerCase());
                              
                              // Gender filter
                              const matchesGender = genderFilter === 'All' || service.gender === genderFilter;
                              
                              // Category filter - handle both categoryId.name and category string
                              let serviceCategoryName = null;
                              if (service.categoryId && typeof service.categoryId === 'object' && service.categoryId.name) {
                                serviceCategoryName = service.categoryId.name;
                              } else if (service.category) {
                                serviceCategoryName = service.category;
                              }
                              
                              const matchesCategory = categoryFilters.length === 0 || 
                                (serviceCategoryName && categoryFilters.includes(serviceCategoryName));
                              
                              return matchesSearch && matchesGender && matchesCategory && !service.isCombo && !service.offerType;
                            })
                            .sort((a, b) => {
                              // First, always sort by displayOrder if both services have it
                              const aOrder = a.displayOrder;
                              const bOrder = b.displayOrder;
                              
                              if (aOrder !== undefined && aOrder !== null && bOrder !== undefined && bOrder !== null) {
                                return Number(aOrder) - Number(bOrder);
                              }
                              
                              // Then apply the selected sort
                              let aValue, bValue;
                              
                              switch (sortBy) {
                                case 'price':
                                  aValue = a.price;
                                  bValue = b.price;
                                  break;
                                case 'rating':
                                  aValue = a.rating;
                                  bValue = b.rating;
                                  break;
                                case 'duration':
                                  aValue = a.duration;
                                  bValue = b.duration;
                                  break;
                                default: // name
                                  aValue = a.name.toLowerCase();
                                  bValue = b.name.toLowerCase();
                                  break;
                              }
                              
                              if (sortOrder === 'asc') {
                                return aValue > bValue ? 1 : -1;
                              } else {
                                return aValue < bValue ? 1 : -1;
                              }
                            })
                            .map((service) => {
                              const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                              return (
                                <Grid item xs={6} sm={4} md={3} lg={2} key={service._id}>
                                  <ServiceCard sx={{ 
                                    width: '100%', 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}>
                                    {/* Image Section */}
                                    <Box 
                                      sx={{ position: 'relative' }}
                                            onClick={() => navigate(`/service/${service._id}/${id}`)}
                                          >
                                      <CardMedia
                                        component="img"
                                        image={service.image || '/placeholder-service.jpg'}
                                              alt={service.name}
                                        sx={{
                                                objectFit: 'cover',
                                          width: '100%',
                                          height: { xs: 140, md: 160 },
                                          cursor: 'pointer'
                                              }}
                                              onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                      
                                      {/* Featured Badge */}
                                      {service.isFeatured && (
                                        <Chip
                                          label="Featured"
                                          sx={{
                                            position: 'absolute',
                                            top: 6,
                                            left: 6,
                                            backgroundColor: '#ffd700',
                                            color: '#333',
                                            fontWeight: 'bold',
                                            fontSize: '0.5rem',
                                            height: 18,
                                            '& .MuiChip-label': {
                                              fontSize: '0.5rem',
                                              px: 0.5
                                            }
                                          }}
                                        />
                                      )}
                                          </Box>

                                    {/* Content Section */}
                                    <CardContent sx={{ 
                                      flexGrow: 1, 
                                      p: { xs: 1, md: 1.5 },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'space-between'
                                    }}>
                                          <Typography 
                                            variant="h6" 
                                            sx={{ 
                                              fontWeight: 'bold', 
                                              mb: 0.5, 
                                          fontSize: { xs: '0.8rem', md: '0.9rem' },
                                              cursor: 'pointer',
                                              '&:hover': { color: 'primary.main' }
                                            }}
                                            onClick={() => navigate(`/service/${service._id}/${id}`)}
                                          >
                                            {service.name}
                                          </Typography>
                                      
                                          <Typography 
                                            variant="body2" 
                                        color="text.secondary" 
                                        sx={{ mb: 0.5, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                                      >
                                        by {provider?.businessName || provider?.name || 'Provider'}
                                          </Typography>

                                      {/* Price and Duration */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                              ${service.price}
                                            </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                          <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                                              {formatDuration(service.duration)}
                                            </Typography>
                                          </Box>
                                          </Box>
                                      
                                      {/* Category and Gender */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Chip 
                                          label={service.category} 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ 
                                            fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                                            height: 18,
                                            '& .MuiChip-label': {
                                              px: 0.5
                                            }
                                          }}
                                        />
                                        {getGenderIcon(service.gender)}
                                      </Box>

                                      {/* Cart Controls */}
                                          {cartItem ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <IconButton 
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  updateCartItemQuantity(service._id, cartItem.quantity - 1);
                                                }}
                                                sx={{ 
                                                  bgcolor: '#f5f5f5',
                                              border: '1px solid #ddd',
                                                  width: 24,
                                                  height: 24,
                                                  '&:hover': { bgcolor: '#e0e0e0' }
                                                }}
                                              >
                                            <Remove sx={{ fontSize: 16 }} />
                                              </IconButton>
                                          <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.8rem' }}>
                                                {cartItem.quantity}
                                              </Typography>
                                              <IconButton 
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  updateCartItemQuantity(service._id, cartItem.quantity + 1);
                                                }}
                                                sx={{ 
                                              bgcolor: '#1a237e',
                                                  color: 'white',
                                                  width: 24,
                                                  height: 24,
                                              '&:hover': { bgcolor: '#0d47a1' }
                                                }}
                                              >
                                            <Add sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </Box>
                                          ) : (
                                            <Button
                                              variant="contained"
                                          fullWidth
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddToCart(service);
                                              }}
                                              sx={{ 
                                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                                            minHeight: '32px',
                                            maxHeight: '32px',
                                                py: 0.5,
                                            bgcolor: '#1a237e',
                                            '&:hover': { bgcolor: '#0d47a1' },
                                            textTransform: 'none'
                                              }}
                                            >
                                              Add to Cart
                                            </Button>
                                          )}
                                    </CardContent>
                                  </ServiceCard>
                                </Grid>
                              );
                            })}
                        </Grid>
                      )}
                    </Box>
                  )}

                  {/* Featured Services Section */}
                  {provider.services.filter(s => {
                    // Apply all filters to featured services too
                    const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      s.description.toLowerCase().includes(searchQuery.toLowerCase());
                    const matchesGender = genderFilter === 'All' || s.gender === genderFilter;
                    const matchesCategory = categoryFilters.length === 0 || categoryFilters.includes(s.category);
                    return s.isFeatured && matchesSearch && matchesGender && matchesCategory;
                  }).length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Star sx={{ color: '#ffd700' }} />
                        Featured Services
                      </Typography>
                      <Grid container spacing={{ xs: 2, md: 3 }}>
                        {provider.services
                          .filter(service => {
                            // Apply all filters
                            const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              service.description.toLowerCase().includes(searchQuery.toLowerCase());
                            const matchesGender = genderFilter === 'All' || service.gender === genderFilter;
                            const matchesCategory = categoryFilters.length === 0 || categoryFilters.includes(service.category);
                            return service.isFeatured && matchesSearch && matchesGender && matchesCategory;
                          })
                          .sort((a, b) => {
                            // Sort by displayOrder (same pattern as price, name, etc.)
                            const aOrder = a.displayOrder;
                            const bOrder = b.displayOrder;
                            
                            // Sort by displayOrder first (if both have it)
                            if (aOrder !== undefined && aOrder !== null && bOrder !== undefined && bOrder !== null) {
                              return Number(aOrder) - Number(bOrder);
                            }
                            
                            // Services with displayOrder come before those without
                            if (aOrder !== undefined && aOrder !== null) return -1;
                            if (bOrder !== undefined && bOrder !== null) return 1;
                            
                            // If both have no displayOrder, sort by name alphabetically
                            return a.name.localeCompare(b.name);
                          })
                          .map((service) => {
                            const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                            return (
                              <Grid item xs={6} sm={4} md={3} lg={2} key={service._id}>
                                <ServiceCard sx={{ 
                                  width: '100%', 
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                    border: '2px solid #ffd700'
                                }}>
                                  {/* Image Section */}
                                  <Box 
                                    sx={{ position: 'relative' }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                    <CardMedia
                                      component="img"
                                      image={service.image || '/placeholder-service.jpg'}
                                            alt={service.name}
                                      sx={{
                                              objectFit: 'cover',
                                        width: '100%',
                                        height: { xs: 140, md: 160 },
                                        cursor: 'pointer'
                                            }}
                                            onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    
                                    {/* Featured Badge */}
                                    <Chip
                                      label="Featured"
                                      sx={{
                                        position: 'absolute',
                                        top: 6,
                                        left: 6,
                                        backgroundColor: '#ffd700',
                                        color: '#333',
                                        fontWeight: 'bold',
                                        fontSize: '0.5rem',
                                        height: 18,
                                        '& .MuiChip-label': {
                                          fontSize: '0.5rem',
                                          px: 0.5
                                        }
                                            }}
                                          />
                                        </Box>

                                  {/* Content Section */}
                                  <CardContent sx={{ 
                                    flexGrow: 1, 
                                    p: { xs: 1, md: 1.5 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                  }}>
                                        <Typography 
                                          variant="h6" 
                                          sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 0.5, 
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                            cursor: 'pointer',
                                            '&:hover': { color: 'primary.main' }
                                          }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                          {service.name}
                                        </Typography>
                                    
                                        <Typography 
                                          variant="body2" 
                                      color="text.secondary" 
                                      sx={{ mb: 0.5, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                                        >
                                          by {provider?.businessName || provider?.name || 'Provider'}
                                        </Typography>

                                    {/* Price and Duration */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                                            ${service.price}
                                          </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                            <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>
                                          {formatDuration(service.duration)}
                                            </Typography>
                                          </Box>
                                        </Box>
                                    
                                    {/* Category and Gender */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Chip 
                                        label={service.category} 
                                        size="small" 
                                        variant="outlined"
                                          sx={{
                                          fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                                          height: 18,
                                          '& .MuiChip-label': {
                                            px: 0.5
                                          }
                                        }}
                                      />
                                          {getGenderIcon(service.gender)}
                                        </Box>

                                    {/* Cart Controls */}
                                        {cartItem ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton 
                                              size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateCartItemQuantity(service._id, cartItem.quantity - 1);
                                          }}
                                              sx={{ 
                                            bgcolor: '#f5f5f5',
                                            border: '1px solid #ddd',
                                            width: 24,
                                            height: 24,
                                            '&:hover': { bgcolor: '#e0e0e0' }
                                          }}
                                        >
                                          <Remove sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.8rem' }}>
                                              {cartItem.quantity}
                                            </Typography>
                                            <IconButton 
                                              size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateCartItemQuantity(service._id, cartItem.quantity + 1);
                                          }}
                                              sx={{ 
                                            bgcolor: '#1a237e',
                                                color: 'white',
                                            width: 24,
                                            height: 24,
                                            '&:hover': { bgcolor: '#0d47a1' }
                                          }}
                                        >
                                          <Add sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Button
                                            variant="contained"
                                        fullWidth
                                            size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddToCart(service);
                                        }}
                                            sx={{ 
                                          fontSize: { xs: '0.65rem', md: '0.7rem' },
                                          minHeight: '32px',
                                          maxHeight: '32px',
                                          py: 0.5,
                                          bgcolor: '#1a237e',
                                          '&:hover': { bgcolor: '#0d47a1' },
                                          textTransform: 'none'
                                            }}
                                          >
                                            Add to Cart
                                          </Button>
                                        )}
                                  </CardContent>
                                </ServiceCard>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Box>
                  )}

                  {/* Combos Section - DISABLED */}
                  {false && provider.services.filter(s => s.isCombo).length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOffer sx={{ color: '#4caf50' }} />
                        Combos & Packages
                      </Typography>
                      <Grid container spacing={3}>
                        {provider.services
                          .filter(service => service.isCombo)
                          .map((service) => {
                            const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                            return (
                              <Grid item xs={12} md={6} key={service._id}>
                                <ServiceCard 
                                  sx={{ 
                                    border: '2px solid #4caf50'
                                  }}
                                >
                                  <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={5}>
                                        <Box
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                          sx={{ cursor: 'pointer' }}
                                        >
                                          <img 
                                            src={service.image || '/placeholder-service.jpg'} 
                                            alt={service.name}
                                            style={{ 
                                              width: '100%', 
                                              height: 140, 
                                              objectFit: 'cover',
                                              borderRadius: 12
                                            }}
                                            onError={(e) => {
                                              e.target.src = '/placeholder-service.jpg';
                                            }}
                                          />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={7}>
                                        <Typography 
                                          variant="h6" 
                                          sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 0.5, 
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            '&:hover': { color: 'primary.main' }
                                          }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                          {service.name}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: '#666',
                                            mb: 1,
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                          }}
                                        >
                                          by {provider?.businessName || provider?.name || 'Provider'}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          color="text.secondary" 
                                          sx={{ 
                                            mb: 1, 
                                            fontSize: '0.8rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            cursor: 'pointer',
                                            '&:hover': { color: '#1976d2' }
                                          }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                          {service.description}
                                          {service.description && service.description.length > 80 && (
                                            <span style={{ color: '#1976d2', fontWeight: 600 }}> ...view more</span>
                                          )}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                            ${service.price}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                              {service.duration} min
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip 
                                              label={service.category} 
                                              size="small" 
                                              variant="outlined"
                                              sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                          </Box>
                                          {getGenderIcon(service.gender) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {getGenderIcon(service.gender)}
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {renderStars(service.rating)}
                                            <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                              {service.rating}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        

                                        {/* Included Services Section - Only show for combo services */}
                                        {service.isCombo && (() => {
                                          // Check for different possible property names for combo services
                                          const comboServices = service.selectedServices || 
                                                               service.services || 
                                                               service.includedServices || 
                                                               service.comboServices ||
                                                               service.serviceIds ||
                                                               service.comboServicesList;
                                          
                                          
                                          if (comboServices && comboServices.length > 0) {
                                            return (
                                              <Box sx={{ mb: 2 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, fontSize: '0.7rem' }}>
                                                  Included Services:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                  {comboServices.map((serviceItem, idx) => {
                                                    let serviceName = '';
                                                    
                                                    if (typeof serviceItem === 'string') {
                                                      // Find the service by ID
                                                      const foundService = provider?.services?.find(s => s._id === serviceItem);
                                                      serviceName = foundService ? foundService.name : serviceItem;
                                                    } else {
                                                      serviceName = serviceItem.name || serviceItem.serviceName || serviceItem;
                                                    }
                                                    
                                                    return (
                                                      <Chip
                                                        key={idx}
                                                        label={serviceName}
                                                        size="small"
                                                        sx={{
                                                          fontSize: '0.6rem',
                                                          height: 20,
                                                          backgroundColor: '#fce4ec',
                                                          color: '#c2185b',
                                                          '& .MuiChip-label': {
                                                            px: 1
                                                          }
                                                        }}
                                                      />
                                                    );
                                                  })}
                                                </Box>
                                              </Box>
                                            );
                                          }
                                          
                                          return (
                                            <Box sx={{ mb: 2, p: 1, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                                              <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                                                No included services found for this combo. Check backend logs.
                                              </Typography>
                                            </Box>
                                          );
                                        })()}
                                        
                                        {/* Inline Cart Controls */}
                                        {cartItem ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity - 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Remove sx={{ fontSize: 12 }} />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ minWidth: 16, textAlign: 'center', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              {cartItem.quantity}
                                            </Typography>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity + 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Add sx={{ fontSize: 12 }} />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleAddToCart(service); }}
                                            sx={{ 
                                              textTransform: 'none',
                                              fontWeight: 'bold',
                                              borderRadius: 2,
                                              bgcolor: '#1976d2',
                                              '&:hover': { bgcolor: '#1565c0' }
                                            }}
                                          >
                                            Add to Cart
                                          </Button>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </ServiceCard>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Box>
                  )}

                  {/* Today's Offers - DISABLED */}
                  {false && provider.services.filter(s => s.offerType === 'today').length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOffer sx={{ color: '#ff6b6b' }} />
                        Today's Offers
                      </Typography>
                      <Grid container spacing={3}>
                        {provider.services
                          .filter(service => service.offerType === 'today')
                          .sort((a, b) => {
                            // Sort by displayOrder (same pattern as price, name, etc.)
                            const aOrder = a.displayOrder;
                            const bOrder = b.displayOrder;
                            
                            // Sort by displayOrder first (if both have it)
                            if (aOrder !== undefined && aOrder !== null && bOrder !== undefined && bOrder !== null) {
                              return Number(aOrder) - Number(bOrder);
                            }
                            
                            // Services with displayOrder come before those without
                            if (aOrder !== undefined && aOrder !== null) return -1;
                            if (bOrder !== undefined && bOrder !== null) return 1;
                            
                            // If both have no displayOrder, sort by name alphabetically
                            return a.name.localeCompare(b.name);
                          })
                          .map((service) => {
                            const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                            return (
                              <Grid item xs={12} md={6} key={service._id}>
                                <ServiceCard sx={{ border: '2px solid #ff6b6b' }}>
                                  <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={5}>
                                        <img 
                                          src={service.image || '/placeholder-service.jpg'} 
                                          alt={service.name}
                                          style={{ 
                                            width: '100%', 
                                            height: 140, 
                                            objectFit: 'cover',
                                            borderRadius: 12
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={7}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                                          {service.name}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          color="text.secondary" 
                                          sx={{ 
                                            mb: 1, 
                                            fontSize: '0.8rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            cursor: 'pointer',
                                            '&:hover': { color: '#1976d2' }
                                          }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                          {service.description}
                                          {service.description && service.description.length > 80 && (
                                            <span style={{ color: '#1976d2', fontWeight: 600 }}> ...view more</span>
                                          )}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                            ${service.price}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                              {service.duration} min
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip 
                                              label={service.category} 
                                              size="small" 
                                              variant="outlined"
                                              sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                            <Chip 
                                              label="Today's Offer" 
                                              size="small" 
                                              color="error"
                                              sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                          </Box>
                                          {getGenderIcon(service.gender) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {getGenderIcon(service.gender)}
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {renderStars(service.rating)}
                                            <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                              {service.rating}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        
                                        {/* Inline Cart Controls */}
                                        {cartItem ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity - 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Remove sx={{ fontSize: 12 }} />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ minWidth: 16, textAlign: 'center', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              {cartItem.quantity}
                                            </Typography>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity + 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Add sx={{ fontSize: 12 }} />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleAddToCart(service); }}
                                            sx={{ 
                                              textTransform: 'none',
                                              fontWeight: 'bold',
                                              borderRadius: 2,
                                              bgcolor: '#1976d2',
                                              '&:hover': { bgcolor: '#1565c0' }
                                            }}
                                          >
                                            Add to Cart
                                          </Button>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </ServiceCard>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Box>
                  )}

                  {/* This Week's Offers - DISABLED */}
                  {false && provider.services.filter(s => s.offerType === 'week').length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOffer sx={{ color: '#ff9800' }} />
                        This Week's Offers
                      </Typography>
                      <Grid container spacing={3}>
                        {provider.services
                          .filter(service => service.offerType === 'week')
                          .map((service) => {
                            const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                            return (
                              <Grid item xs={12} md={6} key={service._id}>
                                <ServiceCard sx={{ border: '2px solid #ff9800' }}>
                                  <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={5}>
                                        <img 
                                          src={service.image || '/placeholder-service.jpg'} 
                                          alt={service.name}
                                          style={{ 
                                            width: '100%', 
                                            height: 140, 
                                            objectFit: 'cover',
                                            borderRadius: 12
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={7}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                                          {service.name}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          color="text.secondary" 
                                          sx={{ 
                                            mb: 1, 
                                            fontSize: '0.8rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            cursor: 'pointer',
                                            '&:hover': { color: '#1976d2' }
                                          }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                          {service.description}
                                          {service.description && service.description.length > 80 && (
                                            <span style={{ color: '#1976d2', fontWeight: 600 }}> ...view more</span>
                                          )}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                            ${service.price}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                              {service.duration} min
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip 
                                              label={service.category} 
                                              size="small" 
                                              variant="outlined"
                                              sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                            <Chip 
                                              label="Week Offer" 
                                              size="small" 
                                              sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#ff9800', color: 'white' }}
                                            />
                                          </Box>
                                          {getGenderIcon(service.gender) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {getGenderIcon(service.gender)}
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {renderStars(service.rating)}
                                            <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                              {service.rating}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        
                                        {/* Inline Cart Controls */}
                                        {cartItem ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity - 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Remove sx={{ fontSize: 12 }} />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ minWidth: 16, textAlign: 'center', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              {cartItem.quantity}
                                            </Typography>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity + 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Add sx={{ fontSize: 12 }} />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleAddToCart(service); }}
                                            sx={{ 
                                              textTransform: 'none',
                                              fontWeight: 'bold',
                                              borderRadius: 2,
                                              bgcolor: '#1976d2',
                                              '&:hover': { bgcolor: '#1565c0' }
                                            }}
                                          >
                                            Add to Cart
                                          </Button>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </ServiceCard>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Box>
                  )}

                  {/* This Month's Offers - DISABLED */}
                  {false && provider.services.filter(s => s.offerType === 'month').length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOffer sx={{ color: '#9c27b0' }} />
                        This Month's Offers
                      </Typography>
                      <Grid container spacing={3}>
                        {provider.services
                          .filter(service => service.offerType === 'month')
                          .map((service) => {
                            const cartItem = isInCart(service._id) ? { quantity: getItemQuantity(service._id) } : null;
                            return (
                              <Grid item xs={12} md={6} key={service._id}>
                                <ServiceCard sx={{ border: '2px solid #9c27b0' }}>
                                  <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={5}>
                                        <img 
                                          src={service.image || '/placeholder-service.jpg'} 
                                          alt={service.name}
                                          style={{ 
                                            width: '100%', 
                                            height: 140, 
                                            objectFit: 'cover',
                                            borderRadius: 12
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={7}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                                          {service.name}
                                        </Typography>
                                        <Typography 
                                          variant="body2" 
                                          color="text.secondary" 
                                          sx={{ 
                                            mb: 1, 
                                            fontSize: '0.8rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            cursor: 'pointer',
                                            '&:hover': { color: '#1976d2' }
                                          }}
                                          onClick={() => navigate(`/service/${service._id}/${id}`)}
                                        >
                                          {service.description}
                                          {service.description && service.description.length > 80 && (
                                            <span style={{ color: '#1976d2', fontWeight: 600 }}> ...view more</span>
                                          )}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                            ${service.price}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Timer sx={{ fontSize: 14, color: '#1976d2' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                              {service.duration} min
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip 
                                              label={service.category} 
                                              size="small" 
                                              variant="outlined"
                                              sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                            <Chip 
                                              label="Month Offer" 
                                              size="small" 
                                              sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#9c27b0', color: 'white' }}
                                            />
                                          </Box>
                                          {getGenderIcon(service.gender) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {getGenderIcon(service.gender)}
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {renderStars(service.rating)}
                                            <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                                              {service.rating}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        
                                        {/* Inline Cart Controls */}
                                        {cartItem ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity - 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Remove sx={{ fontSize: 12 }} />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ minWidth: 16, textAlign: 'center', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                              {cartItem.quantity}
                                            </Typography>
                                            <IconButton 
                                              size="small"
                                              onClick={() => updateCartItemQuantity(service._id, cartItem.quantity + 1)}
                                              sx={{ 
                                                bgcolor: '#1976d2', 
                                                color: 'white',
                                                width: 20,
                                                height: 20,
                                                '&:hover': { bgcolor: '#1565c0' }
                                              }}
                                            >
                                              <Add sx={{ fontSize: 12 }} />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleAddToCart(service); }}
                                            sx={{ 
                                              textTransform: 'none',
                                              fontWeight: 'bold',
                                              borderRadius: 2,
                                              bgcolor: '#1976d2',
                                              '&:hover': { bgcolor: '#1565c0' }
                                            }}
                                          >
                                            Add to Cart
                                          </Button>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </ServiceCard>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Box>
                  )}
                </Box>
              )}

              {/* Reviews Tab */}
              {selectedTab === 1 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Customer Reviews ({provider.reviews?.length || 0})
                  </Typography>
                  
                  {provider.reviews && provider.reviews.length > 0 ? (
                    <Grid container spacing={3}>
                      {provider.reviews.map((review, index) => (
                        <Grid item xs={12} key={index}>
                          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: '#1976d2' }}>
                                    {review.customerName ? review.customerName.charAt(0).toUpperCase() : 'C'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                      {review.customerName || 'Anonymous'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(review.date).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {renderStars(review.rating)}
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {review.rating}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography variant="body1" sx={{ mb: 2 }}>
                                {review.comment}
                              </Typography>
                              {review.serviceName && (
                                <Chip 
                                  label={`Service: ${review.serviceName}`} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <RateReview sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No Reviews Yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Be the first to review this provider!
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

                              {/* Snap Tab (Photo Gallery) */}
                {selectedTab === 2 && (
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                      Photo Gallery
                    </Typography>
                    
                    {provider.photoGallery && provider.photoGallery.length > 0 ? (
                      <Grid container spacing={2}>
                        {provider.photoGallery.map((item, index) => (
                          <Grid item xs={12} sm={6} md={4} key={item._id || index}>
                            <Card 
                              sx={{ 
                                borderRadius: 2, 
                                overflow: 'hidden', 
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                                }
                              }}
                              onClick={() => openStoryViewer(index)}
                            >
                              <Box sx={{ position: 'relative' }}>
                                {item.type === 'video' ? (
                                  <Box sx={{
                                    height: 200,
                                    position: 'relative',
                                    backgroundImage: `url(${item.thumbnail})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <Box sx={{
                                      bgcolor: 'rgba(0,0,0,0.7)',
                                      borderRadius: '50%',
                                      width: 48,
                                      height: 48,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <PlayArrow sx={{ fontSize: 32, color: 'white' }} />
                                    </Box>
                                  </Box>
                                ) : (
                                  <CardMedia
                                    component="img"
                                    height="200"
                                    image={item.url}
                                    alt={item.caption || 'Gallery image'}
                                    sx={{ objectFit: 'cover' }}
                                  />
                                )}
                              </Box>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <PhotoLibrary sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No Showcase Work Available
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This provider hasn't uploaded any showcase work yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* About Tab */}
              {selectedTab === 3 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                    About {provider.name}
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* About Section */}
                    <Grid item xs={12} md={8}>
                      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                            About Us
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                            {provider.aboutUs || provider.description || 'No description available.'}
                          </Typography>
                          
                          {provider.experience && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Experience
                              </Typography>
                              <Typography variant="body1">
                                {provider.experience} years of experience
                              </Typography>
                            </Box>
                          )}
                          
                          {provider.specializations && provider.specializations.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Specializations
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {provider.specializations.map((spec, index) => (
                                  <Chip 
                                    key={index}
                                    label={spec} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Contact & Info Section */}
                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                            Contact Information
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <LocationOn sx={{ color: '#1976d2' }} />
                            <Typography variant="body2">
                              {provider.address || 'Address not available'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Phone sx={{ color: '#1976d2' }} />
                            <Typography variant="body2">
                              {provider.phone || 'Phone not available'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Email sx={{ color: '#1976d2' }} />
                            <Typography variant="body2">
                              {provider.email || 'Email not available'}
                            </Typography>
                          </Box>
                          
                          {provider.website && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Business sx={{ color: '#1976d2' }} />
                              <Typography variant="body2">
                                {provider.website}
                              </Typography>
                            </Box>
                          )}
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                            Business Hours
                          </Typography>
                          
                          {provider.businessHours ? (
                            <Box>
                              {Object.entries(provider.businessHours).map(([day, hours]) => (
                                <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {day}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {hours}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Business hours not available
                            </Typography>
                          )}
                          
                          {/* Social Media Links */}
                          {(provider.socialMedia?.facebook || provider.socialMedia?.instagram || provider.socialMedia?.twitter || provider.socialMedia?.linkedin || provider.website) && (
                            <>
                              <Divider sx={{ my: 3 }} />
                              
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Connect With Us
                              </Typography>
                              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {provider.socialMedia?.facebook && (
                                  <Tooltip title="Follow us on Facebook" arrow>
                                    <IconButton
                                      component="a"
                                      href={provider.socialMedia.facebook.startsWith('http') ? provider.socialMedia.facebook : `https://facebook.com/${provider.socialMedia.facebook}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        bgcolor: '#1877f2',
                                        color: 'white',
                                        width: { xs: 44, md: 48 },
                                        height: { xs: 44, md: 48 },
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          bgcolor: '#1565c0',
                                          transform: 'scale(1.1)',
                                          boxShadow: '0 4px 12px rgba(24, 119, 242, 0.4)'
                                        }
                                      }}
                                    >
                                      <Facebook sx={{ fontSize: { xs: 20, md: 24 } }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                
                                {provider.socialMedia?.instagram && (
                                  <Tooltip title="Follow us on Instagram" arrow>
                                    <IconButton
                                      component="a"
                                      href={provider.socialMedia.instagram.startsWith('http') ? provider.socialMedia.instagram : `https://instagram.com/${provider.socialMedia.instagram}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                        color: 'white',
                                        width: { xs: 44, md: 48 },
                                        height: { xs: 44, md: 48 },
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          transform: 'scale(1.1)',
                                          boxShadow: '0 4px 12px rgba(225, 48, 108, 0.4)'
                                        }
                                      }}
                                    >
                                      <Instagram sx={{ fontSize: { xs: 20, md: 24 } }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                
                                {provider.socialMedia?.twitter && (
                                  <Tooltip title="Follow us on X (Twitter)" arrow>
                                    <IconButton
                                      component="a"
                                      href={provider.socialMedia.twitter.startsWith('http') ? provider.socialMedia.twitter : `https://twitter.com/${provider.socialMedia.twitter}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        bgcolor: '#000000',
                                        color: 'white',
                                        width: { xs: 44, md: 48 },
                                        height: { xs: 44, md: 48 },
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          bgcolor: '#333333',
                                          transform: 'scale(1.1)',
                                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                                        }
                                      }}
                                    >
                                      <svg
                                        viewBox="0 0 24 24"
                                        width="20"
                                        height="20"
                                        fill="currentColor"
                                        style={{ width: '20px', height: '20px' }}
                                      >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                      </svg>
                                    </IconButton>
                                  </Tooltip>
                                )}
                                
                                {provider.socialMedia?.linkedin && (
                                  <Tooltip title="Connect on LinkedIn" arrow>
                                    <IconButton
                                      component="a"
                                      href={provider.socialMedia.linkedin.startsWith('http') ? provider.socialMedia.linkedin : `https://linkedin.com/in/${provider.socialMedia.linkedin}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        bgcolor: '#0077b5',
                                        color: 'white',
                                        width: { xs: 44, md: 48 },
                                        height: { xs: 44, md: 48 },
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          bgcolor: '#006097',
                                          transform: 'scale(1.1)',
                                          boxShadow: '0 4px 12px rgba(0, 119, 181, 0.4)'
                                        }
                                      }}
                                    >
                                      <LinkedIn sx={{ fontSize: { xs: 20, md: 24 } }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                
                                {provider.website && (
                                  <Tooltip title="Visit our website" arrow>
                                    <IconButton
                                      component="a"
                                      href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        bgcolor: '#667eea',
                                        color: 'white',
                                        width: { xs: 44, md: 48 },
                                        height: { xs: 44, md: 48 },
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                          bgcolor: '#5568d3',
                                          transform: 'scale(1.1)',
                                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                        }
                                      }}
                                    >
                                      <Language sx={{ fontSize: { xs: 20, md: 24 } }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </CardContent>
        </ProfileCard>
      </Box>

      {/* Story Viewer Modal */}
      <Dialog 
        open={showStoryViewer} 
        onClose={closeStoryViewer}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'black',
            borderRadius: isMobile ? 0 : 2,
            maxWidth: isMobile ? '100vw' : '90vw',
            maxHeight: isMobile ? '100vh' : '90vh',
            width: isMobile ? '100vw' : '90vw',
            height: isMobile ? '100vh' : '90vh'
          }
        }}
      >
        {showStoryViewer && provider?.photoGallery && (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Header */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              p: 2,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                  {provider.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {provider.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {currentStoryIndex + 1} of {provider.photoGallery.length}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={closeStoryViewer} sx={{ color: 'white' }}>
                <Close />
              </IconButton>
            </Box>

            {/* Progress Bar */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 11,
              p: 2
            }}>
              <Box sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center'
              }}>
                {provider.photoGallery.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: 1,
                      height: 2,
                      bgcolor: 'rgba(255,255,255,0.3)',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: index <= currentStoryIndex ? '100%' : '0%',
                        height: '100%',
                        bgcolor: 'white',
                        transition: 'width 0.3s ease',
                        animation: index === currentStoryIndex ? 'progress 5s linear' : 'none'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
                              {provider.photoGallery[currentStoryIndex].type === 'video' ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <video
                    src={provider.photoGallery[currentStoryIndex].url}
                    controls={false}
                    autoPlay={isVideoPlaying}
                    loop
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  {!isVideoPlaying && (
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      bgcolor: 'rgba(0,0,0,0.7)',
                      borderRadius: '50%',
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={toggleVideoPlay}
                  >
                    <PlayArrow sx={{ fontSize: 48, color: 'white' }} />
                  </Box>
                  )}
                </Box>
              ) : (
                <Box
                  component="img"
                  src={provider.photoGallery[currentStoryIndex].url}
                  alt={provider.photoGallery[currentStoryIndex].caption}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </Box>

            {/* Caption */}
            {provider.photoGallery[currentStoryIndex].caption && (
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 3,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                zIndex: 10
              }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  {provider.photoGallery[currentStoryIndex].caption}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {new Date(provider.photoGallery[currentStoryIndex].timestamp).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            {/* Navigation Buttons */}
            {!isMobile && (
              <>
                {currentStoryIndex > 0 && (
                  <IconButton
                    onClick={prevStory}
                    sx={{
                      position: 'absolute',
                      left: 20,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                )}
                {currentStoryIndex < provider.photoGallery.length - 1 && (
                  <IconButton
                    onClick={nextStory}
                    sx={{
                      position: 'absolute',
                      right: 20,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                )}
              </>
            )}

            {/* Touch Navigation for Mobile */}
            {isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 5
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const width = rect.width;
                  
                  if (x < width / 3) {
                    prevStory();
                  } else if (x > (width * 2) / 3) {
                    nextStory();
                  }
                }}
              />
            )}
          </Box>
        )}
      </Dialog>

    </Box>
  );
};

export default ProviderProfile; 
